import { Validators }                        from '@angular/forms';
import { gv }                                from '@cs/core/utils';
import { CompareOperator, CsValidatorsUtils } from './cs-validators.utils';
import { IValidationAnnotation }             from '../../data-described/validators/validation-annotations';

export { CompareOperator };


export enum ValidationAnnotationType {
	Regex        = 'pattern',
	Range        = 'range',
	Required     = 'required',
	Maxlength    = 'maxlength',
	Minlength    = 'minlength',
	InputMask    = 'inputmask',
	AwbNumber    = 'awbnumber',
	ServerError  = 'servererror',
	CompareField = 'compareField',
	Number       = 'number'
}

export class AwbNumberValidationAnnotation implements IValidationAnnotation<ValidationAnnotationType> {

	type = ValidationAnnotationType.AwbNumber;

	warningMessage?: string;
	errorMessage?: string;

	rem: number;
	serial: number;
	check: number;

	constructor(init: Partial<AwbNumberValidationAnnotation>) {
		this.warningMessage = init.warningMessage;
		this.errorMessage   = init.errorMessage;
	}

	validatorFunc() {
		return [CsValidatorsUtils.awbNumber()];
	}
}


export class RangeValidationAnnotation implements IValidationAnnotation<ValidationAnnotationType> {
	type = ValidationAnnotationType.Range;
	minimum: number;
	maximum: number;
	errorMessage: string;

	constructor(init: Partial<RangeValidationAnnotation>) {
		this.minimum      = init.minimum;
		this.maximum      = init.maximum;
		this.errorMessage = init.errorMessage;
	}

	validatorFunc() {
		return [CsValidatorsUtils.range(this.minimum, this.maximum)];
	}
}

export class DateRangeValidationAnnotation implements IValidationAnnotation<ValidationAnnotationType> {
	type = ValidationAnnotationType.Range;
	minimum: any;
	maximum: any;
	errorMessage: string;

	constructor(init: Partial<RangeValidationAnnotation>) {
		// Store raw values - can be Date, timestamp, or DateStruct
		this.minimum      = init.minimum;
		this.maximum      = init.maximum;
		this.errorMessage = init.errorMessage;
	}

	validatorFunc() {
		// Convert to timestamps for validation
		const minTimestamp = this.toTimestamp(this.minimum);
		const maxTimestamp = this.toTimestamp(this.maximum);
		return [CsValidatorsUtils.dateRange(minTimestamp, maxTimestamp)];
	}

	/**
	 * Convert various date formats to timestamp (seconds since epoch)
	 */
	private toTimestamp(value: any): number {
		if (value == null) return null;
		
		// Already a number (timestamp)
		if (typeof value === 'number') {
			return value;
		}
		
		// DateStruct format { year, month, day }
		if (typeof value === 'object' && value.year && value.month && value.day) {
			return new Date(value.year, value.month - 1, value.day).getTime() / 1000.0;
		}
		
		// Date object or date string
		const dateObj = new Date(value);
		if (!isNaN(dateObj.getTime())) {
			return dateObj.getTime() / 1000.0;
		}
		
		return null;
	}
}

export class RegexValidationAnnotation implements IValidationAnnotation<ValidationAnnotationType> {
	type = ValidationAnnotationType.Regex;
	pattern: string;
	errorMessage: string;

	constructor(init: Partial<RegexValidationAnnotation>) {
		this.pattern      = init.pattern;
		this.errorMessage = init.errorMessage;
	}

	validatorFunc() {
		return [Validators.pattern(this.pattern)];
	}
}

export class RequiredValidationAnnotation implements IValidationAnnotation<ValidationAnnotationType> {
	type = ValidationAnnotationType.Required;
	errorMessage: string;

	constructor(init: Partial<RequiredValidationAnnotation>) {
		this.errorMessage = gv(() => init.errorMessage, 'VALUE_IS_REQUIRED');
	}

	validatorFunc() {
		return [Validators.required];
	}
}

export class InputMaskValidationAnnotation implements IValidationAnnotation<ValidationAnnotationType> {
	type = ValidationAnnotationType.InputMask;
	errorMessage: string;
	warningMessage: string;

	constructor(init: Partial<InputMaskValidationAnnotation>) {
		this.errorMessage   = init.errorMessage;
		this.warningMessage = init.warningMessage;
	}

	validatorFunc() {
		return [];
	}
}

export class DefaultServerValidationAnnotation implements IValidationAnnotation<ValidationAnnotationType> {
	type = ValidationAnnotationType.ServerError;

	constructor() {
	}

	validatorFunc() {
		return [CsValidatorsUtils.serverError()];
	}
}

export class NumberValidationAnnotation implements IValidationAnnotation<ValidationAnnotationType> {
	type = ValidationAnnotationType.Number;
	errorMessage: string;

	constructor(init: Partial<NumberValidationAnnotation>) {
		this.errorMessage = init.errorMessage;
	}

	validatorFunc() {
		return [CsValidatorsUtils.numeric()];
	}
}

export class MaxlengthValidationAnnotation implements IValidationAnnotation<ValidationAnnotationType> {
	type = ValidationAnnotationType.Maxlength;
	value: number;
	errorMessage: string;

	constructor(init: Partial<MaxlengthValidationAnnotation>) {
		this.value        = init.value;
		this.errorMessage = init.errorMessage;
	}

	validatorFunc() {
		return [Validators.maxLength(this.value)];
	}
}

export class MinlengthValidationAnnotation implements IValidationAnnotation<ValidationAnnotationType> {
	type = ValidationAnnotationType.Minlength;
	value: number;
	errorMessage: string;

	constructor(init: Partial<MinlengthValidationAnnotation>) {
		this.value        = init.value;
		this.errorMessage = init.errorMessage;
	}

	validatorFunc() {
		return [Validators.minLength(this.value)];
	}
}

export class CompareFieldValidationAnnotation implements IValidationAnnotation<ValidationAnnotationType> {
	type = ValidationAnnotationType.CompareField;
	compareField: string;
	operator: CompareOperator;
	errorMessage: string;

	constructor(init: Partial<CompareFieldValidationAnnotation>) {
		this.compareField = init.compareField;
		this.operator     = init.operator || 'lessThan';
		this.errorMessage = init.errorMessage;
	}

	/**
	 * Note: This validator needs access to the FormGroup, so the actual validator
	 * function is created in the form generator parser with access to the formGroup.
	 */
	validatorFunc() {
		// Returns empty array - actual validator is created in parser with formGroup reference
		return [];
	}
}
